import React, { useCallback, useState, useEffect, useRef } from "react";
import { useLocales } from "src/locales";

import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { debounce } from "@mui/material/utils";
import { useGoogleMapsPlacesContext } from "./useGoogleMapsPlacesContext";
import { useQuery } from "@tanstack/react-query";

export function AutoCompleteGooglePlaces({
    cityData,
    handleChange,
    label,
    placeholder,
    language = "en", // Default language is English
    textFieldSx,
    textFieldExtraProps = null,
    sx,
    allowCountry = true,
    inputLabelProps = {},
    ...other
}) {
    const { t } = useLocales();
    const { fetchPlaceSuggestions, getPlaceById } =
        useGoogleMapsPlacesContext();

    // ✅ Ensure cityData is used as the initial value
    const [selectedValue, setSelectedValue] = useState(cityData || null);

    const [inputText, setInputText] = useState("");

    const firstLoadPlaceData = useRef(true);

    const getSuggestions = async () => {
        try {
            if (!inputText || inputText?.length === 0) return [];

            let predictions;

            predictions = await fetchPlaceSuggestions(inputText, language);

            if (!allowCountry) {
                predictions = predictions?.filter(
                    (prediction) => !prediction?.types.includes("country"),
                );
            }

            const formattedOptions = predictions.map((place) => ({
                label: place.description,
                cityLabel: place.structured_formatting?.secondary_text ?? "",
                placeId: place.place_id,
            }));
            return formattedOptions;
        } catch (error) {
            console.error("Error fetchPlaceSuggestions", error);
        }
    };

    const {
        data: suggestions,
        isLoading: isLoadingSuggestions,
        isFetching: isFetchingSuggestions,
    } = useQuery({
        queryKey: ["suggestions", inputText],
        queryFn: getSuggestions,
        enabled: !!inputText,
        placeholderData: [],
        staleTime: Infinity,
    });

    const getPlaceData = async () => {
        try {
            if (!selectedValue?.placeId) return null;

            if (cityData?.placeId === selectedValue?.placeId) return cityData;

            const value = selectedValue;

            const placeData = await getPlaceById(
                value.placeId,
                value.label,
                language,
            );
            const newValue = {
                label: value.label,
                cityLabel: value.cityLabel,
                name: placeData.city,
                city: placeData.city,
                country: placeData.country,
                countryCode: placeData.countryCode,
                latitude: placeData.latitude,
                longitude: placeData.longitude,
                isAddress: placeData.isAddress,
                placeId: value.placeId,
                adressName: placeData.adressName,
            };

            return newValue;
        } catch (error) {
            console.error("Error getPlaceData", error);
        }
    };

    const { data: placeData } = useQuery({
        queryKey: ["placeData", selectedValue?.placeId, language],
        queryFn: getPlaceData,
        placeholderData: null,
        staleTime: Infinity,
    });

    useEffect(() => {
        if (!cityData) return;

        setSelectedValue(cityData || null);
    }, [cityData]);

    useEffect(() => {
        if (firstLoadPlaceData.current) {
            firstLoadPlaceData.current = false;
            return;
        }

        if (!placeData && selectedValue?.placeId) return;

        handleChange(placeData);
    }, [placeData]);

    const onInputChange = useCallback(
        debounce(async (input) => {
            if (!input) {
                return;
            }
            setInputText(input);
        }, 1000),
        [fetchPlaceSuggestions, language],
    );

    const handlePlaceChange = useCallback(
        async (value) => {
            setSelectedValue(value || null);
        },
        [handleChange, language],
    );

    const parseOptionLabel = useCallback((option) => {
        if (!option) return "";
        if (option.label) return option.label;
        return `${option.city || option.name}, ${option.country}`;
    }, []);

    return (
        <Box sx={sx}>
            <Autocomplete
                id="city-search"
                getOptionLabel={(option) => parseOptionLabel(option)}
                options={suggestions} // Use the options specific to this city
                loading={isLoadingSuggestions || isFetchingSuggestions}
                loadingText={t("Loading...")}
                fullWidth
                autoComplete
                onChange={(_, value) => handlePlaceChange(value)}
                value={selectedValue} // ✅ Use value instead of defaultValue
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        fullWidth
                        onChange={(e) => onInputChange(e.target.value)}
                        placeholder={placeholder}
                        sx={textFieldSx}
                        InputLabelProps={{
                            ...params.InputLabelProps,
                            ...inputLabelProps,
                        }}
                        {...textFieldExtraProps}
                    />
                )}
                {...other}
            />
        </Box>
    );
}
